import {
  type AuthenticationResponseJSON,
  browserSupportsWebAuthnAutofill,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  WebAuthnError,
} from "@simplewebauthn/browser";
import { api } from "./shared";

// The server plugin asks for "preferred"; enthused-id requires the PIN or biometric step and the
// server rejects assertions without it, so ask the authenticator for it up front.

const authenticationOptions = () =>
  api<PublicKeyCredentialRequestOptionsJSON>("/passkey/generate-authenticate-options");

const choosePasskey = (
  options: PublicKeyCredentialRequestOptionsJSON,
  useBrowserAutofill: boolean,
) =>
  startAuthentication({
    optionsJSON: { ...options, userVerification: "required" },
    useBrowserAutofill,
  });

const verify = (response: AuthenticationResponseJSON) =>
  api<{ url?: string }>("/passkey/verify-authentication", { response });

/** Set once the button is pressed: from then on the autofill offer stands down. */
let buttonTookOver = false;

/**
 * The button: cancel the waiting conditional request first (only one WebAuthn ceremony may be
 * open at a time), then ask for a passkey with the browser's own prompt. An offer still fetching
 * its options sees `buttonTookOver` and never starts, since starting would abort this ceremony.
 */
export async function signInWithPasskey() {
  buttonTookOver = true;
  WebAuthnAbortService.cancelCeremony();
  return verify(await choosePasskey(await authenticationOptions(), false));
}

/** enthused-id couldn't start a passkey sign-in: the server, not the person or the device. */
export class PasskeysUnavailable extends Error {
  override readonly name = "PasskeysUnavailable";
}

/**
 * Conditional mediation: offer the device's passkeys in the email box's autofill, where
 * `PublicKeyCredential.isConditionalMediationAvailable()` says the browser can (that is what
 * `browserSupportsWebAuthnAutofill` asks). Resolves with the sign-in result when the person picks
 * one, and with undefined when the ceremony ends with nothing picked: the browser can't offer
 * passkeys here (headless Chromium and devices without an authenticator refuse with
 * NotSupportedError), or the button took over (before the ceremony started, or by aborting it). The person asked for neither, so neither
 * is an error. Rejects with PasskeysUnavailable when the options request fails, and with the
 * error when the ceremony fails any other way or a chosen passkey fails verification.
 */
export async function offerPasskeyAutofill() {
  if (!(await browserSupportsWebAuthnAutofill())) return undefined;
  let options: PublicKeyCredentialRequestOptionsJSON;
  try {
    options = await authenticationOptions();
  } catch (error) {
    if (buttonTookOver) return undefined;
    throw new PasskeysUnavailable("Couldn't fetch passkey options", { cause: error });
  }
  if (buttonTookOver) return undefined;
  let response: AuthenticationResponseJSON;
  try {
    response = await choosePasskey(options, true);
  } catch (error) {
    if (cancelled(error) || notSupported(error)) return undefined;
    throw error;
  }
  return verify(response);
}

const notSupported = (error: unknown) =>
  error instanceof Error &&
  (error.name === "NotSupportedError" ||
    (error.cause instanceof Error && error.cause.name === "NotSupportedError"));

export async function addPasskey() {
  const options = await api<PublicKeyCredentialCreationOptionsJSON>(
    "/passkey/generate-register-options",
  );
  const response = await startRegistration({
    optionsJSON: {
      ...options,
      authenticatorSelection: { ...options.authenticatorSelection, userVerification: "required" },
    },
  });
  return api("/passkey/verify-registration", { response });
}

/** The person closed the prompt or it timed out: not an error worth shouting about. */
export const cancelled = (error: unknown) =>
  (error instanceof WebAuthnError && error.code === "ERROR_CEREMONY_ABORTED") ||
  (error instanceof Error && error.name === "NotAllowedError");
